import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { NavBar } from '../components/NavBar'
import { supabase } from '../lib/supabase'
import { formatDateLong } from '../lib/dateUtils'
import { useWantToWatch } from '../hooks/useWantToWatch'
import { WantToWatchEntry, ProductionType } from '../types'
import { Button } from '../components/ui/button'

const TYPE_LABELS: Record<ProductionType, string> = {
  live: 'Live',
  filmed_stage: 'Filmed Stage',
  feature_film: 'Feature Film',
  television: 'Television',
  audio: 'Audio',
  immersive: 'Immersive',
  adaptation: 'Adaptation',
}

function WantToWatchCard({
  entry,
  onRemove,
  onLogIt,
}: {
  entry: WantToWatchEntry
  onRemove: (productionId: string) => void
  onLogIt: (entry: WantToWatchEntry) => void
}) {
  const production = entry.productions
  const play = production?.plays

  return (
    <div
      data-testid="want-to-watch-card"
      className="flex gap-4 rounded-lg border border-stone-800 bg-stone-800/40 p-4"
    >
      {production?.poster_image_url ? (
        <img
          src={production.poster_image_url}
          alt="Production poster"
          className="h-24 w-16 flex-shrink-0 rounded object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-24 w-16 flex-shrink-0 items-center justify-center rounded bg-stone-700"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6 text-stone-500"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
            />
          </svg>
        </div>
      )}

      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-serif text-sm font-medium text-stone-400">
          {play?.title ?? 'Unknown play'}
        </p>
        <h3 className="font-serif text-base font-semibold leading-snug text-stone-100">
          {production?.company_or_studio ?? production?.title ?? play?.title ?? 'Unknown production'}
        </h3>
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {production?.type && (
            <span className="rounded-full bg-stone-700 px-2.5 py-0.5 text-xs text-stone-300">
              {TYPE_LABELS[production.type] ?? production.type}
            </span>
          )}
          {production?.year && (
            <span className="text-xs text-stone-500">{production.year}</span>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-col items-end justify-between gap-2">
        <Button
          size="sm"
          className="bg-red-900 text-xs text-stone-100 hover:bg-red-800"
          onClick={() => onLogIt(entry)}
        >
          Log it
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRemove(entry.production_id)}
          className="text-xs text-stone-500 hover:text-red-400"
        >
          Remove
        </Button>
      </div>
    </div>
  )
}

export function WantToWatchPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { entries, loading, error, remove } = useWantToWatch(user?.id)

  const [duplicateDialog, setDuplicateDialog] = useState<{
    entry: WantToWatchEntry
    priorDate: string
    productionLabel: string
  } | null>(null)

  async function handleLogIt(entry: WantToWatchEntry) {
    const { data: existing } = await supabase
      .from('log_entries')
      .select('date_watched')
      .eq('user_id', user?.id)
      .eq('production_id', entry.production_id)
      .order('date_watched', { ascending: false })
      .limit(1)

    if (existing && existing.length > 0) {
      const prod = entry.productions
      const productionLabel = prod
        ? (prod.company_or_studio ?? prod.title ?? prod.plays?.title ?? 'this production')
        : 'this production'
      setDuplicateDialog({
        entry,
        priorDate: formatDateLong(existing[0].date_watched),
        productionLabel,
      })
      return
    }

    navigate(`/log?production_id=${entry.production_id}`)
  }

  function handleConfirmDuplicate() {
    if (!duplicateDialog) return
    const { entry } = duplicateDialog
    setDuplicateDialog(null)
    navigate(`/log?production_id=${entry.production_id}&skip_duplicate_check=1`)
  }

  function handleCancelDuplicate() {
    setDuplicateDialog(null)
  }

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100">
      {duplicateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={handleCancelDuplicate}
          />
          <div className="relative z-10 mx-4 max-w-sm rounded-lg border border-stone-700 bg-stone-900 p-6 shadow-xl">
            <h2 className="mb-3 font-serif text-lg font-semibold text-stone-100">
              Already logged
            </h2>
            <p className="mb-6 text-sm text-stone-300">
              You already logged{' '}
              <span className="font-medium text-stone-100">
                {duplicateDialog.productionLabel}
              </span>{' '}
              on {duplicateDialog.priorDate}. Log it again?
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={handleConfirmDuplicate}
                className="flex-1 bg-red-900 text-stone-100 hover:bg-red-800"
              >
                Log it again
              </Button>
              <Button
                type="button"
                onClick={handleCancelDuplicate}
                className="flex-1 border border-stone-600 bg-transparent text-stone-300 hover:bg-stone-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      <NavBar />
      <div className="px-6 py-10">
      <div className="mx-auto max-w-2xl">
        <h1 className="mb-8 font-serif text-3xl font-semibold tracking-tight">
          Want to watch
        </h1>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex gap-4 rounded-lg border border-stone-800 bg-stone-800/40 p-4"
              >
                <div className="h-24 w-16 flex-shrink-0 rounded bg-stone-700" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-3 w-24 rounded bg-stone-700" />
                  <div className="h-4 w-40 rounded bg-stone-700" />
                  <div className="h-3 w-16 rounded bg-stone-700" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-400">
            Failed to load your list: {error}
          </p>
        ) : entries.length === 0 ? (
          <div
            data-testid="empty-state"
            className="py-20 text-center"
          >
            <p className="font-serif text-2xl font-semibold text-stone-300">
              Want to watch
            </p>
            <p className="mt-3 text-sm text-stone-500">
              Productions you're saving for later will appear here
            </p>
            <Link
              to="/plays"
              className="mt-6 inline-block text-sm text-stone-400 transition-colors hover:text-amber-400"
            >
              Browse plays →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <WantToWatchCard
                key={entry.id}
                entry={entry}
                onRemove={remove}
                onLogIt={handleLogIt}
              />
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}
